import React from "react";
import { ProjectUpdate } from "tekkr-common/dist/model/project/project-update";
import { DateTime } from "luxon";
import { cn } from "../../../lib/utils";
import { Trash2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { useConfirmDialog } from "../../../components/modals/confirmation-dialog-provider";
import { getScopeLabel } from "../scope-utils";

interface Props {
    updates: ProjectUpdate[];
    selectedUpdateId: string | null;
    onSelectUpdate: (updateId: string | null) => void;
    onDeleteUpdate: (updateId: string) => void;
    projectName: string;
}

export function UpdateHistorySidebar({
    updates,
    selectedUpdateId,
    onSelectUpdate,
    onDeleteUpdate,
    projectName,
}: Props) {
    const confirm = useConfirmDialog();

    const handleDelete = (e: React.MouseEvent, update: ProjectUpdate) => {
        e.stopPropagation();
        confirm(
            {
                title: "Delete Update",
                body: (
                    <div className="flex flex-col gap-3 pb-3">
                        <div>
                            Are you sure you want to delete this status update from{" "}
                            <strong className="font-semibold">{getScopeLabel(update.scope)}</strong>?
                        </div>
                        <div className="text-sm text-muted-foreground">This action cannot be undone.</div>
                    </div>
                ),
                confirmButtonText: "Delete",
                confirmButtonVariant: "destructive",
            },
            () => onDeleteUpdate(update.id)
        )();
    };

    return (
        <div className="flex w-full max-w-xs shrink-0 flex-col gap-1 border-r p-4">
            <div className="flex shrink-0 flex-col gap-3">
                <span className="text-md line-clamp-3 max-w-64 text-start font-semibold" title={projectName}>
                    {projectName}
                </span>
                <Button onClick={() => onSelectUpdate(null)} className="w-full justify-center" size="sm">
                    New Update
                </Button>
                <span className="mt-4 px-2 text-sm font-normal text-muted-foreground">Sent Updates</span>
            </div>

            {updates.length === 0 && (
                <div className="px-2 py-8 text-center text-sm text-muted-foreground">No updates sent yet.</div>
            )}

            {updates.length > 0 && (
                <div className="min-h-0 flex-1 overflow-y-auto">
                    <div className="flex flex-col gap-1 pt-2">
                        {[...updates].reverse().map((update) => (
                            <div
                                key={update.id}
                                className={cn(
                                    "group flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent",
                                    selectedUpdateId === update.id && "bg-accent"
                                )}
                                onClick={() => onSelectUpdate(update.id)}
                            >
                                <div className="flex flex-col gap-0.5 overflow-hidden">
                                    <span className="truncate font-medium">{update.title}</span>
                                    <span className="text-xs text-muted-foreground">
                                        {DateTime.fromISO(update.sentAt).toLocaleString({
                                            month: "short",
                                            day: "numeric",
                                            hour: "numeric",
                                            minute: "2-digit",
                                        })}
                                    </span>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100"
                                    onClick={(e) => handleDelete(e, update)}
                                >
                                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
